import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewChildren,
  QueryList,
  NgZone,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbWindowRef, NbToastrService } from '@nebular/theme';
import { EquipmentService } from '../../../../@core/services/equipment.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicListViewComponent } from '../../../../shared/component/dynamiclistview/dynamiclistview.component';
import { SlicerComponent } from '../../../inventory/components/slicer/slicer.component';

interface Equipment {
  id: string;
  equipmentNum: string;
  serial: string;
  make: string;
  model: string;
  locationNum: string;
  category: string;
  status: string;
}

interface LocationCount {
  value: string;
  count: number;
}

interface IFilter {
  label: string;
  field: string;
  selectedValues: string[];
  values?: any[];
  isLoading?: boolean;
}

@Component({
    selector: 'stack-mono-equipment-counting-journal-batch-create',
    templateUrl: './equipment-counting-journal-batch-create.component.html',
    styleUrls: ['./equipment-counting-journal-batch-create.component.scss'],
    standalone: false
})
export class EquipmentCountingJournalBatchCreateComponent
  implements OnInit, OnDestroy
{
  @ViewChild('equipmentList') equipmentList: DynamicListViewComponent;
  @ViewChildren('slicer') slicers: QueryList<SlicerComponent>;

  form: FormGroup;
  loading = false;
  selectedEquipment: Equipment[] = [];
  totalEquipmentCount = 0;
  readonly MAX_EQUIPMENT_COUNT = 20000;
  locationCounts: LocationCount[] = [];
  loadingLocationCounts = false;
  private destroy$ = new Subject<void>();
  //Location, Fleet Type, Status, Station, and Branch
  filters: IFilter[] = [
    {
      label: 'Location',
      field: 'locationNum',
      selectedValues: [],
    },
    {
      label: 'Fleet Type',
      field: 'fleetType',
      selectedValues: [],
    },
    {
      label: 'Status',
      field: 'status',
      selectedValues: [],
    },
    {
      label: 'Station',
      field: 'station',
      selectedValues: [],
    },

    {
      label: 'Branch',
      field: 'branchNum',
      selectedValues: [],
    },
  ];

  constructor(
    private fb: FormBuilder,
    private windowRef: NbWindowRef,
    private equipmentService: EquipmentService,
    private toastr: NbToastrService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      name: ['', Validators.required],
      type: ['PhysicalCount', Validators.required], // Default to Physical Inventory
      splitByLocation: [true], // Always default to true
      blindCount: [true], // Default to true
      requireConfirmSerial: [true], // Default to false
    });

    // Fetch location counts on init since splitByLocation is always true
    this.fetchLocationCounts();

    // Initialize slicer values
    this.getSlicerLabelsCounts(true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEquipmentSelect(equipment: Equipment[]): void {
    this.selectedEquipment = equipment || [];
  }

  onTotalCountChange(count: number): void {
    this.totalEquipmentCount = count;
    // Refresh location counts when total count changes (filter changed)
    this.fetchLocationCounts();
  }

  onSlicerChange(filter: IFilter, selectedValues: any[]): void {
    const newSelectedValues = selectedValues.map((slicer) => slicer.value);

    // Only update selectedValues, don't replace the values array
    // The slicer component manages its own checked state
    const targetFilter = this.filters.find((f) => f.field === filter.field);
    if (targetFilter) {
      targetFilter.selectedValues = newSelectedValues;
    }
    this.applySlicerFilters();
  }

  applySlicerFilters(): void {
    if (!this.equipmentList) {
      return;
    }

    const slicerFilters = this.filters
      .filter((f) => f.selectedValues.length > 0)
      .map((f) => ({
        column: f.field,
        value: f.selectedValues.join(','),
      }));

    this.equipmentList.externalFilters = slicerFilters;
    this.equipmentList.refreshGrid();
    this.fetchLocationCounts();
  }

  clearFilters(): void {
    this.filters = this.filters.map((f) => ({ ...f, selectedValues: [] }));
    this.slicers?.forEach((slicer) => {
      slicer.tags = [];
    });
    this.applySlicerFilters();
    this.getSlicerLabelsCounts(true);
  }

  get showClearFilter(): boolean {
    return this.filters.some((f) => f.selectedValues.length > 0);
  }

  getSlicerLabelsCounts(showLoading = true): void {
    const fields = this.filters.map((filter) => {
      if (showLoading) {
        filter.isLoading = true;
      }
      return filter.field;
    });

    const slicerFilters = this.filters
      .filter((f) => f.selectedValues.length > 0)
      .map((f) => ({
        column: f.field,
        value: f.selectedValues.join(','),
      }));

    const search = {
      first: 9000,
      filter: slicerFilters,
      skip: 0,
    };

    this.equipmentService
      .getEquipmentFieldGroupByCount(fields, search)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.filters.forEach((filter) => {
          filter.isLoading = false;
          this.ngZone.run(() => {
            const fieldData = res.data['getEquipmentFieldGroupByCount']?.find(
              (item: any) => item.field === filter.field,
            );
            filter.values =
              fieldData?.values?.map((item: any) => ({
                value: item.value,
                count: item.count,
                checked: filter.selectedValues.includes(item.value),
              })) || [];
          });
        });
      });
  }

  fetchLocationCounts(): void {
    if (!this.equipmentList?.gridApi) {
      return;
    }

    this.loadingLocationCounts = true;

    // Get current filters from the grid
    const filterModel = this.equipmentList.gridApi.getFilterModel();
    const gridFilters = this.normalizeGridFilter(filterModel);

    // Combine with any external filters
    const combinedFilters = [
      ...(this.equipmentList.filters || []),
      ...gridFilters,
      ...(this.equipmentList.externalFilters || []),
    ];

    const search = {
      filter: combinedFilters,
      first: 1000,
      skip: 0,
    };

    this.equipmentService
      .getEquipmentFieldGroupByCount(['locationNum'], search)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result: any) => {
          const data = result?.data?.getEquipmentFieldGroupByCount;
          if (data && data.length > 0) {
            this.locationCounts = data[0].values || [];
          } else {
            this.locationCounts = [];
          }
          this.loadingLocationCounts = false;
        },
        error: (error) => {
          console.error('Error fetching location counts:', error);
          this.locationCounts = [];
          this.loadingLocationCounts = false;
        },
      });
  }

  private normalizeGridFilter(filterModel: any): any[] {
    if (!filterModel) return [];
    const filters: any[] = [];
    Object.keys(filterModel).forEach((key) => {
      const filter = filterModel[key];
      if (filter.filter) {
        filters.push({
          column: key,
          value: `${filter.filter}`,
        });
      }
    });
    return filters;
  }

  getTotalEquipmentCount(): number {
    return this.locationCounts.reduce((sum, loc) => sum + loc.count, 0);
  }

  get isEquipmentCountExceeded(): boolean {
    return this.totalEquipmentCount > this.MAX_EQUIPMENT_COUNT;
  }

  close() {
    this.windowRef.close();
  }

  private getFilterFromGrid(): any {
    if (!this.equipmentList?.gridApi) {
      return { filter: [], first: 1000, skip: 0 };
    }

    // Get current filters from the grid
    const filterModel = this.equipmentList.gridApi.getFilterModel();
    const gridFilters = this.normalizeGridFilter(filterModel);

    // Combine with any external filters
    const combinedFilters = [
      ...(this.equipmentList.filters || []),
      ...gridFilters,
      ...(this.equipmentList.externalFilters || []),
    ];

    return {
      filter: combinedFilters,
      first: 10000,
      skip: 0,
    };
  }

  async submit() {
    if (
      this.form.invalid ||
      this.totalEquipmentCount === 0 ||
      this.isEquipmentCountExceeded
    ) {
      return;
    }

    this.loading = true;
    const value = this.form.value;

    const dto = {
      name: value.name,
      type: value.type,
      splitByLocation: value.splitByLocation,
      blindCount: value.blindCount,
      requireConfirmSerial: value.requireConfirmSerial,
      filter: this.getFilterFromGrid(),
    };

    try {
      await this.equipmentService
        .batchCreateEquipmentCountingJournals(dto)
        .toPromise();

      this.toastr.success(
        'Batch journal creation job has been queued',
        'Success',
      );

      // Close window with result to trigger refresh
      this.windowRef.close({ refresh: true });
    } catch (error) {
      console.error(error);
      this.toastr.danger('Failed to queue journal creation', 'Error');
      this.loading = false;
    }
  }
}
